/**
 * Messages Component - Phase 3 Communication System
 * Main messaging interface for host-influencer communication
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { UserType } from '../../constants/userTypes';
import { useLocaleFormatters } from '../../utils/localeFormatters';
import {
  Message,
  Conversation,
  SendMessageInput
} from '../../types';
import {
  GET_MESSAGES,
  GET_CONVERSATIONS_BY_HOST_ID,
  GET_CONVERSATIONS_BY_INFLUENCER_ID
} from '../../graphql/queries';
import {
  SEND_MESSAGE,
  MARK_MESSAGE_READ
} from '../../graphql/mutations';
import {
  ON_MESSAGE_RECEIVED
} from '../../graphql/subscriptions';
import ConversationsList from './ConversationsList';
import MessageInput from './MessageInput';
import { Spinner } from '../common/Spinner';

// Simple MessageThread component inline
const MessageThread: React.FC<{
  messages: Message[];
  currentUserId: string;
  formatRelativeTime: (date: Date | string) => string;
}> = ({ messages, currentUserId, formatRelativeTime }) => {
  const formatTime = (createdAt: string): string => {
    return formatRelativeTime(createdAt);
  };

  const getInitials = (userId: string): string => {
    return userId.substring(0, 2).toUpperCase();
  };

  const getUserAvatar = (userId: string): JSX.Element => {
    const initials = getInitials(userId);
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    const colorIndex = userId.length % colors.length;

    return (
      <div className={`w-8 h-8 rounded-full ${colors[colorIndex]} flex items-center justify-center text-white text-sm font-medium`}>
        {initials}
      </div>
    );
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">No messages yet</h3>
          <p className="text-gray-500">Start a conversation by sending your first message!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => {
        const isOwnMessage = message.senderId === currentUserId;
        const isSystemMessage = message.messageType === 'system';

        if (isSystemMessage) {
          return (
            <div key={message.messageId} className="flex justify-center">
              <p className="text-sm text-gray-500 italic bg-gray-100 px-3 py-1 rounded-full inline-block">
                {message.content}
              </p>
            </div>
          );
        }

        return (
          <div
            key={message.messageId}
            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} space-x-2`}
          >
            {!isOwnMessage && (
              <div className="flex-shrink-0">
                {getUserAvatar(message.senderId)}
              </div>
            )}

            <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-1' : ''}`}>
              <div
                className={`rounded-lg px-4 py-2 ${
                  isOwnMessage
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>

              <div className={`flex items-center mt-1 space-x-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                <span className="text-xs text-gray-500">
                  {formatTime(message.createdAt)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface MessagesProps {
  selectedConversationId?: string;
  campaignId?: string;
  matchId?: string;
}

const Messages: React.FC<MessagesProps> = ({
  selectedConversationId,
  campaignId,
  matchId
}) => {
  const { user } = useAuthStore();
  const location = useLocation();
  const { formatRelativeTime } = useLocaleFormatters();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(
    selectedConversationId || null
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [userProfileId, setUserProfileId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // User type detection effect - using user.userType from auth store
  useEffect(() => {
    const detectUserType = async () => {
      console.log('🔍 Starting user type detection...', { userId: user?.userId, userType: user?.userType });
      if (!user?.userId) return;

      try {
        // Use the userType from auth store to determine which profile to load
        if (user.userType === 'host') {
          // User is logged in as host
          const hostResponse = await graphqlApi.getHostProfileByUserId(user.userId);
          if (hostResponse.success && hostResponse.data) {
            console.log('👤 User detected as HOST for conversations:', hostResponse.data.hostId);
            setUserType('host');
            setUserProfileId(hostResponse.data.hostId);
            return;
          }
        } else if (user.userType === 'influencer') {
          // User is logged in as influencer
          const influencerResponse = await graphqlApi.getInfluencerByUserId(user.userId);
          if (influencerResponse.success && influencerResponse.data) {
            console.log('🌟 User detected as INFLUENCER for conversations:', influencerResponse.data.influencerId);
            setUserType('influencer');
            setUserProfileId(influencerResponse.data.influencerId);
            return;
          }
        }

        console.log('⚠️ User has no profile for userType:', user.userType);
        setUserType(null);
        setUserProfileId(null);
      } catch (error) {
        console.error('Error detecting user type:', error);
        setUserType(null);
        setUserProfileId(null);
      }
    };

    detectUserType();
  }, [user?.userId, user?.userType]);

  // User-type specific conversation queries
  // Dynamically select query based on current userType state
  const selectedQuery = useMemo(() => {
    console.log('🔧 Selecting conversations query for userType:', userType);
    if (userType === 'host') {
      console.log('✅ Selected GET_CONVERSATIONS_BY_HOST_ID');
      return GET_CONVERSATIONS_BY_HOST_ID;
    } else if (userType === 'influencer') {
      console.log('✅ Selected GET_CONVERSATIONS_BY_INFLUENCER_ID');
      return GET_CONVERSATIONS_BY_INFLUENCER_ID;
    }
    console.log('⚠️ No query selected (userType not determined), using host query as placeholder');
    return GET_CONVERSATIONS_BY_HOST_ID; // Placeholder, will be skipped anyway
  }, [userType]);

  const conversationsVariables = useMemo(() => {
    if (userType === 'host') {
      return { hostId: userProfileId, limit: 20 };
    } else if (userType === 'influencer') {
      return { influencerId: userProfileId, limit: 20 };
    }
    return {};
  }, [userType, userProfileId]);

  const shouldSkipQuery = !user || !userType || !userProfileId;

  const {
    data: conversationsData,
    loading: conversationsLoading,
    refetch: refetchConversations
  } = useQuery(selectedQuery, {
    variables: conversationsVariables,
    skip: shouldSkipQuery, // Skip if no query selected or missing data
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!shouldSkipQuery) {
        console.log('🔄 Conversations query completed for', userType, 'with data:', data);
      }
    },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      if (!shouldSkipQuery) {
        console.error('❌ Conversations query error for', userType, ':', error);
      }
    }
  });

  const {
    data: messagesData,
    loading: messagesLoading,
    refetch: refetchMessages,
    fetchMore
  } = useQuery(GET_MESSAGES, {
    variables: {
      conversationId: activeConversationId,
      limit: 50
    },
    skip: !activeConversationId,
  });

  // Mutations
  const [sendMessage] = useMutation(SEND_MESSAGE, {
    refetchQueries: [
      // Refetch the appropriate conversations query based on user type
      ...(userType === 'host' && userProfileId ? [{
        query: GET_CONVERSATIONS_BY_HOST_ID,
        variables: { hostId: userProfileId, limit: 20 }
      }] : []),
      ...(userType === 'influencer' && userProfileId ? [{
        query: GET_CONVERSATIONS_BY_INFLUENCER_ID,
        variables: { influencerId: userProfileId, limit: 20 }
      }] : []),
      { query: GET_MESSAGES, variables: { conversationId: activeConversationId, limit: 50 } }
    ],
    onError: (error) => {
      console.error('Send message error:', error);
      alert('Failed to send message: ' + error.message);
    }
  });

  const [markAsRead] = useMutation(MARK_MESSAGE_READ);

  // 🚀 REAL-TIME WEBSOCKET SUBSCRIPTIONS using Amplify API
  useEffect(() => {
    if (!activeConversationId) return;

    console.log('📡 Setting up message subscription for conversation:', activeConversationId);

    // Subscribe to new messages
    const messageSubscription = (
      API.graphql(
        graphqlOperation(ON_MESSAGE_RECEIVED, { conversationId: activeConversationId })
      ) as any
    ).subscribe({
      next: ({ value }: any) => {
        console.log('🔔 Real-time message received via WebSocket:', value);

        const newMessage = value.data?.onMessageReceived;
        if (newMessage && newMessage.senderId !== user?.userId) {
          setMessages(prevMessages => {
            // Check if message already exists to avoid duplicates
            const messageExists = prevMessages.some(msg => msg.messageId === newMessage.messageId);
            if (!messageExists) {
              console.log('✅ Adding new real-time message to conversation');
              return [...prevMessages, newMessage];
            }
            return prevMessages;
          });

          // Auto-scroll to bottom for new messages
          setTimeout(scrollToBottom, 100);

          // Mark message as read if conversation is active
          if (activeConversationId === newMessage.conversationId) {
            markAsRead({ variables: { messageId: newMessage.messageId } });
          }
        }
      },
      error: (error: any) => {
        console.error('❌ WebSocket message subscription error:', error);
      }
    });

    // Cleanup subscription on unmount or conversation change
    return () => {
      console.log('🔌 Unsubscribing from message updates');
      messageSubscription.unsubscribe();
    };
  }, [activeConversationId, user?.userId, markAsRead]);

  // Effects
  useEffect(() => {
    // Handle conversations data updates
    if (conversationsData) {
      // Extract conversations based on user type
      const conversations = userType === 'host'
        ? conversationsData.getConversationsByHostId?.conversations
        : conversationsData.getConversationsByInfluencerId?.conversations;

      console.log('📋 Extracted conversations for', userType, ':', conversations);
      setConversations(conversations || []);

      // Auto-select first conversation if none selected
      if (!activeConversationId && conversations && conversations.length > 0) {
        setActiveConversationId(conversations[0].conversationId);
      }
    }
  }, [conversationsData, activeConversationId, userType]);

  useEffect(() => {
    // Handle messages data updates
    if (messagesData?.getMessages) {
      setMessages(messagesData.getMessages.messages);
    }
  }, [messagesData]);

  useEffect(() => {
    // Handle navigation from match card
    if (location.state) {
      const { campaignId: navCampaignId, matchId: navMatchId, userId: targetUserId } = location.state;

      if (navCampaignId && navMatchId && targetUserId) {
        // Find existing conversation for this match/campaign
        const existingConversation = conversations.find(conv =>
          conv.campaignId === navCampaignId &&
          conv.matchId === navMatchId &&
          conv.participants.includes(targetUserId)
        );

        if (existingConversation) {
          setActiveConversationId(existingConversation.conversationId);
        }
        // Note: If no conversation exists, it will be created when the first message is sent
      }
    }
  }, [location.state, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Mark messages as read when conversation changes
    if (activeConversationId && messages.length > 0) {
      const unreadMessages = messages.filter(msg =>
        !msg.readStatus.isRead && msg.senderId !== user?.userId
      );

      unreadMessages.forEach(msg => {
        markAsRead({ variables: { messageId: msg.messageId } });
      });
    }
  }, [activeConversationId, messages, user?.userId, markAsRead]);

  // Handlers
  const handleConversationSelect = (conversationId: string) => {
    setActiveConversationId(conversationId);
  };

  const handleSendMessage = async (content: string, attachments?: any[]) => {
    if (!user || !content.trim()) return;

    // If we don't have an active conversation, try to create one from navigation state
    if (!activeConversationId && location.state) {
      const { campaignId: navCampaignId, matchId: navMatchId, userId: targetUserId } = location.state;

      if (navCampaignId && navMatchId && targetUserId) {
        try {
          console.log('🔍 Checking for existing conversation...', { navCampaignId, navMatchId, targetUserId });

          // Import graphqlApi here to avoid circular dependency
          const { graphqlApi } = await import('../../services/graphql');

          // Check if conversation already exists for this match
          const existingConversation = await graphqlApi.getConversationByMatchId(navMatchId);

          let result: any;
          if (existingConversation.success && existingConversation.data) {
            console.log('✅ Conversation already exists:', existingConversation.data.conversationId);
            result = existingConversation;
          } else {
            console.log('🔄 Creating new conversation for match:', navMatchId);

            // Get campaign and match data to extract hostId and influencerId
            const [campaignResponse, matchResponse] = await Promise.all([
              graphqlApi.getCampaign(navCampaignId),
              graphqlApi.getMatch(navMatchId)
            ]);

            if (!campaignResponse.success || !campaignResponse.data) {
              throw new Error('Failed to get campaign data');
            }
            if (!matchResponse.success || !matchResponse.data) {
              throw new Error('Failed to get match data');
            }

            const campaign = campaignResponse.data;
            const match = matchResponse.data;
            const hostId = campaign.hostId;
            const influencerId = match.influencerId;

            console.log('📋 Conversation participants:', { hostId, influencerId });
            result = await graphqlApi.createConversation(navCampaignId, navMatchId, hostId, influencerId);
          }

          if (result.success && result.data) {
            // Set the new conversation as active
            setActiveConversationId(result.data.conversationId);

            // Add to conversations list
            setConversations(prev => [result.data, ...prev]);

            // Continue with sending the message after conversation is created
            // The message will be sent with the new conversationId below
          } else {
            console.error('Failed to create conversation:', result.error);
            return;
          }
        } catch (error) {
          console.error('Error creating conversation:', error);
          return;
        }
      }
    }

    // Find current conversation to get receiver
    const currentConversation = conversations.find(c => c.conversationId === activeConversationId);
    if (!currentConversation || !activeConversationId) return;

    // After creating conversation or finding existing one, we should have an activeConversationId
    // But TypeScript needs explicit check
    const conversationId = activeConversationId;
    if (!conversationId) return;

    const receiverId = currentConversation.participants.find(p => p !== user.userId);
    if (!receiverId) return;

    const messageInput: SendMessageInput = {
      conversationId: conversationId,
      receiverId,
      messageType: 'text',
      content: content.trim(),
      attachments,
      campaignId,
      matchId
    };

    try {
      await sendMessage({ variables: { input: messageInput } });
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleLoadMore = () => {
    if ((messagesData as any)?.getMessages.nextToken) {
      fetchMore({
        variables: {
          nextToken: (messagesData as any).getMessages.nextToken
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            getMessages: {
              ...(fetchMoreResult as any).getMessages,
              messages: [
                ...(prev as any).getMessages.messages,
                ...(fetchMoreResult as any).getMessages.messages
              ]
            }
          };
        }
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Render
  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">Please log in to access messages.</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Conversations Sidebar */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Messages</h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversationsLoading ? (
            <div className="flex items-center justify-center p-8">
              <Spinner />
            </div>
          ) : (
            <ConversationsList
              conversations={conversations}
              activeConversationId={activeConversationId}
              onConversationSelect={handleConversationSelect}
              currentUserId={user.userId}
            />
          )}
        </div>
      </div>

      {/* Message Thread */}
      <div className="flex-1 flex flex-col">
        {activeConversationId ? (
          <>
            {/* Conversation Header */}
            <div className="border-b border-gray-200 px-6 py-4 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {conversations.find(c => c.conversationId === activeConversationId)?.title || 'Conversation'}
                  </h2>
                  {conversations.find(c => c.conversationId === activeConversationId)?.matchId && (
                    <p className="text-sm text-gray-500 mt-1">Active Application</p>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner />
                </div>
              ) : (
                <>
                  {/* Load More Button */}
                  {(messagesData as any)?.getMessages.nextToken && (
                    <div className="text-center">
                      <button
                        onClick={handleLoadMore}
                        className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Load more messages...
                      </button>
                    </div>
                  )}

                  <MessageThread
                    messages={messages}
                    currentUserId={user.userId}
                    formatRelativeTime={formatRelativeTime}
                  />

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200 p-4">
              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={messagesLoading}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-lg font-medium">Select a conversation to start messaging</p>
              <p className="text-sm mt-2">Choose from your conversations on the left to begin chatting.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
